import { observable, action } from 'mobx';
import _ from 'lodash';
import axios from 'axios';
import ValidationData from './validation/ValidationStore';
import {
  getFormDataFromStorage,
} from './storage/storage';

class OverviewStore {
  allFixedRateTerms = [1, 5, 10, 15, 20, 30];

  @observable accessor calculationForm = {};
  @observable accessor sortedOverviewTableColumn = '';
  @observable accessor sortedFixedRateTerm = 20;
  @observable accessor openSortingDropDown;

  /* Error messages */
  @observable accessor responseError = '';
  @observable accessor errorFields = [];

  /* Default show 10 providers */
  @observable accessor showAllProviders = false;

  constructor() {
    this.sortLabelInterestsByInitialInterest = this.sortLabelInterestsByInitialInterest.bind(this);
    this.sortLabelInterestsByTermInterest = this.sortLabelInterestsByTermInterest.bind(this);
    this.sortLabelInterests = this.sortLabelInterests.bind(this);
    this.getFixedRateTermIndex = this.getFixedRateTermIndex.bind(this);
    this.toggleShowAllProviders = this.toggleShowAllProviders.bind(this);
    this.sortLabelInterestsByTermInterestAvailable = this.sortLabelInterestsByTermInterestAvailable.bind(this);
    this.setSortedOverviewTableColumn = this.setSortedOverviewTableColumn.bind(this);
    this.setSortedFixedRateTerm = this.setSortedFixedRateTerm.bind(this);
    this.closeOpenSortingDropdown = this.closeOpenSortingDropdown.bind(this);
    this.toggleOpenSortingDropdown = this.toggleOpenSortingDropdown.bind(this);
    this.calculateInterestSavings = this.calculateInterestSavings.bind(this);
    this.calculateLoanparts = this.calculateLoanparts.bind(this);
  }

  @action toggleShowAllProviders() {
    this.showAllProviders = !this.showAllProviders;
  }

  @action setSortedOverviewTableColumn(sortedOverviewTableColumn) {
    this.sortedOverviewTableColumn = sortedOverviewTableColumn;
  }

  @action setSortedFixedRateTerm(frt) {
    this.sortedFixedRateTerm = frt;
  }

  @action toggleOpenSortingDropdown() {
    this.openSortingDropDown = !this.openSortingDropDown;
  }

  @action closeOpenSortingDropdown() {
    this.openSortingDropDown = '';
  }

  @action getFixedRateTermIndex(fixedRateTerm) {
    return this.allFixedRateTerms.indexOf(fixedRateTerm);
  }

  /* Calculate savings with and without looptijdrente for the first graph */
  @action
  async calculateInterestSavings(labelInterests, labelId, frt, formData) {
    let promise;
    formData = getFormDataFromStorage(formData);

    if (labelId) {
      promise = await axios.post('/calculate/interest-savings', {
        labelId,
        mortgage: formData.mortgage,
        houseValue: formData.mortgage,
        nhg: formData.nhg,
        loanparts: [{
          gross: formData.mortgage,
          frt,
          loanpartDuration: 360,
          mortgageType: formData.mortgageType,
        }],
      })
      .then(response => response)
      .catch(err => ValidationData.handleResponseError(err));
    }

    return promise;
  }

  /* Calculate loanparts for overview in SavingsContainer */
  @action
  async calculateLoanparts(labelId, frt, formData) {
    formData = getFormDataFromStorage(formData);

    this.calculationForm = {
      labelId,
      mortgage: formData.mortgage,
      houseValue: formData.mortgage,
      nhg: formData.nhg,
      loanparts: [{
        gross: formData.mortgage,
        frt,
        loanpartDuration: 360,
        mortgageType: formData.mortgageType,
      }],
    };

    const res = await axios.post(
     '/calculate/loanparts', this.calculationForm)
    .then(response => response)
    .catch(err => ValidationData.handleResponseError(err));

    return res;
  }

  /**
   *
   * @param fixedRateTerm
   */
  @action sortLabelInterestsByInitialInterest(fixedRateTerm, labelInterests) {
    const labels = labelInterests.slice();
    const fixedRateTermIndex = this.getFixedRateTermIndex(fixedRateTerm);
    let sortedLabelInterests = labelInterests;

    if (this.sortedOverviewTableColumn !== 'initialInterest') {
      this.sortedOverviewTableColumn = 'initialInterest';

      sortedLabelInterests = this.sortLabelInterests(labels, fixedRateTermIndex, 'initialInterest');
    }

    return sortedLabelInterests;
  }

  /**
   *
   * @param fixedRateTerm
   */
  @action sortLabelInterestsByTermInterest(fixedRateTerm, labelInterests) {
    const labels = labelInterests.slice();
    const fixedRateTermIndex = this.getFixedRateTermIndex(fixedRateTerm);
    let sortedLabelInterests = labelInterests;

    if (this.sortedOverviewTableColumn !== 'averageInterest') {
      this.sortedOverviewTableColumn = 'averageInterest';

      sortedLabelInterests = this.sortLabelInterests(labels, fixedRateTermIndex, 'averageInterest');
    }

    return sortedLabelInterests;
  }

  @action sortLabelInterests(labels, fixedRateTermIndex, interestType) {
    return labels.sort((a, b) => {
      let sortedRow;
      if (a.interests[fixedRateTermIndex][interestType] === 0) {
        sortedRow = 1;
      } else if (b.interests[fixedRateTermIndex][interestType] === 0) {
        sortedRow = -1;
      } else if (a.interests.length > fixedRateTermIndex && b.interests.length > fixedRateTermIndex) {
        sortedRow = a.interests[fixedRateTermIndex][interestType] - b.interests[fixedRateTermIndex][interestType];
      } else if (a.interests.length <= fixedRateTermIndex) {
        sortedRow = 1;
      } else if (b.interests.length <= fixedRateTermIndex) {
        sortedRow = -1;
      }

      return sortedRow;
    });
  }

  // eslint-disable-next-line consistent-return
  @action sortLabelInterestsByTermInterestAvailable(labelInterests) {
    let sortedLabelInterests = labelInterests;

    if (this.sortedOverviewTableColumn !== 'averageInterestAvailable') {
      /* Sort by second column */
      this.sortedOverviewTableColumn = 'averageInterestAvailable';

      /* Get all labelInterests with hasAutoRiskClassReduction property */
      const allLabelsInterestsWithHasAutoRiskClassReduction = labelInterests.filter(labelInterest => (
        labelInterest.hasAutoRiskClassReduction
      ));

      const allLabelsInterestsWithoutHasAutoRiskClassReduction = labelInterests
        .filter(labelInterest => labelInterest.hasAutoRiskClassReduction === false);

      sortedLabelInterests = [...allLabelsInterestsWithHasAutoRiskClassReduction,
        ...allLabelsInterestsWithoutHasAutoRiskClassReduction];
    }

    return sortedLabelInterests;
  }

}

const OverviewData = new OverviewStore();

export default OverviewData;
